import { useRequiredContext } from "@redotech/react-util/context";
import CalendarIcon from "@redotech/redo-icons/calendar-date.svg";
import GiftIcon from "@redotech/redo-icons/gift-01_filled.svg";
import HeartIcon from "@redotech/redo-icons/heart_filled.svg";
import StarIcon from "@redotech/redo-icons/star-01_filled.svg";
import TrophyIcon from "@redotech/redo-icons/trophy-01.svg";
import {
  LoyaltyPromotion,
  LoyaltyPromotionRecurringPattern,
} from "@redotech/redo-loyalty-common/loyalty-definition";
import {
  EmptyStateIcon,
  LoyaltyBorderStyle,
  PromotionsBlock,
} from "@redotech/redo-loyalty-common/loyalty-page-builder/definition";
import { memo } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { LoyaltyPageContext } from "../context/loyalty-page-context";

export const Promotions = memo(function Promotions({
  title,
  titleColor,
  subtitle,
  subtitleColor,
  promotions,
  headerFontFamily,
  bodyFontFamily,
  borderStyle,
  emptyState,
}: PromotionsBlock) {
  const context = useRequiredContext(LoyaltyPageContext);
  const loyaltyPromotions = context.loyaltyPromotions.filter(
    (promotion) => promotion.active && !promotion.isDeleted,
  );

  const currentDate = new Date();

  const activePromotions = loyaltyPromotions.filter((promotion) => {
    if (promotion.recurringPattern !== LoyaltyPromotionRecurringPattern.NONE) {
      return true;
    }

    const startDate = promotion.startDate
      ? new Date(promotion.startDate)
      : null;
    const endDate = promotion.endDate ? new Date(promotion.endDate) : null;

    const isWithinDateRange =
      (!startDate || currentDate >= startDate) &&
      (!endDate || currentDate <= endDate);

    return isWithinDateRange;
  });

  const upcomingPromotions = loyaltyPromotions.filter((promotion) => {
    if (promotion.recurringPattern !== LoyaltyPromotionRecurringPattern.NONE) {
      return false;
    }

    const startDate = promotion.startDate
      ? new Date(promotion.startDate)
      : null;
    return startDate && currentDate < startDate;
  });

  return (
    <Flex dir="column" gap="3xl" px="xl" py="6xl" w="full">
      <Text
        fontSize="2xl"
        fontWeight="bold"
        style={{ color: titleColor || "#000000", fontFamily: headerFontFamily }}
      >
        {title || "Special Promotions"}
      </Text>

      <Text
        fontSize="xs"
        style={{
          color: subtitleColor || "#666666",
          fontFamily: bodyFontFamily,
        }}
      >
        {subtitle || "Current offers and upcoming deals"}
      </Text>

      <Flex dir="column" gap="4xl" w="full">
        {activePromotions.length > 0 && (
          <Flex dir="column" gap="lg">
            <Text
              fontSize="lg"
              fontWeight="semibold"
              style={{ fontFamily: headerFontFamily }}
            >
              Active Promotions
            </Text>
            <Flex dir="column" gap="lg" w="full">
              {activePromotions.map((promotion) => (
                <PromotionTile
                  bodyFontFamily={bodyFontFamily}
                  borderStyle={borderStyle}
                  contentColor={promotions.contentColor}
                  key={promotion._id}
                  promotion={promotion}
                  titleColor={promotions.titleColor}
                />
              ))}
            </Flex>
          </Flex>
        )}

        {upcomingPromotions.length > 0 && (
          <Flex dir="column" gap="lg">
            <Text
              fontSize="lg"
              fontWeight="semibold"
              style={{ fontFamily: headerFontFamily }}
            >
              Upcoming Promotions
            </Text>
            <Flex dir="column" gap="lg" w="full">
              {upcomingPromotions.map((promotion) => (
                <PromotionTile
                  bodyFontFamily={bodyFontFamily}
                  borderStyle={borderStyle}
                  contentColor={promotions.contentColor}
                  key={promotion._id}
                  promotion={promotion}
                  titleColor={promotions.titleColor}
                />
              ))}
            </Flex>
          </Flex>
        )}

        {activePromotions.length === 0 && upcomingPromotions.length === 0 && (
          <Flex align="center" dir="column" gap="lg" justify="center" py="6xl">
            <EmptyStateIconComponent icon={emptyState.icon} />
            <Text
              fontSize="lg"
              style={{
                fontFamily: bodyFontFamily,
                color: emptyState.messageColor,
              }}
              textAlign="center"
            >
              {emptyState.message}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

const PromotionTile = memo(function PromotionTile({
  promotion,
  borderStyle,
  bodyFontFamily,
  titleColor,
  contentColor,
}: {
  promotion: LoyaltyPromotion;
  borderStyle: LoyaltyBorderStyle;
  bodyFontFamily: string;
  titleColor: string;
  contentColor: string;
}) {
  const getPromotionDateRange = () => {
    if (promotion.recurringPattern !== LoyaltyPromotionRecurringPattern.NONE) {
      const patternMap: Record<LoyaltyPromotionRecurringPattern, string> = {
        [LoyaltyPromotionRecurringPattern.NONE]: "One-time",
        [LoyaltyPromotionRecurringPattern.ALWAYS]: "Always active",
        [LoyaltyPromotionRecurringPattern.WEEKLY]: "Weekly",
        [LoyaltyPromotionRecurringPattern.MONTHLY]: "Monthly",
      };
      return patternMap[promotion.recurringPattern!] || "Recurring";
    }

    const startDate = promotion.startDate
      ? new Date(promotion.startDate)
      : null;
    const endDate = promotion.endDate ? new Date(promotion.endDate) : null;

    if (startDate && endDate) {
      const formatDate = (date: Date) => {
        const month = date.toLocaleDateString("en-US", { month: "short" });
        const day = date.getDate();
        return `${month} ${day}`;
      };
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    } else if (startDate) {
      return `Starting ${startDate.toLocaleDateString()}`;
    } else if (endDate) {
      return `Until ${endDate.toLocaleDateString()}`;
    }

    return "Limited time";
  };

  return (
    <Flex
      borderColor="primary"
      borderStyle={borderStyle === LoyaltyBorderStyle.BORDER ? "solid" : "none"}
      borderWidth="1px"
      dir="column"
      gap="none"
      p="2xl"
      radius="sm"
      w="full"
    >
      <Text
        fontSize="sm"
        fontWeight="semibold"
        style={{ fontFamily: bodyFontFamily, color: titleColor }}
      >
        {promotion.name}
      </Text>
      <Text
        fontSize="xs"
        style={{ fontFamily: bodyFontFamily, color: contentColor }}
      >
        {promotion.description}
      </Text>
      <Flex align="center" dir="row" gap="xs" mt="sm">
        <CalendarIcon color="tertiary" flexShrink={0} height={14} width={14} />
        <Text
          fontSize="xs"
          style={{ fontFamily: bodyFontFamily }}
          textColor="tertiary"
        >
          {getPromotionDateRange()}
        </Text>
      </Flex>
    </Flex>
  );
});

const EmptyStateIconComponent = memo(function EmptyStateIconComponent({
  icon,
  size = 48,
}: {
  icon: EmptyStateIcon;
  size?: number;
}) {
  switch (icon) {
    case EmptyStateIcon.GIFT:
      return <GiftIcon height={size} width={size} />;
    case EmptyStateIcon.STAR:
      return <StarIcon height={size} width={size} />;
    case EmptyStateIcon.TROPHY:
      return <TrophyIcon height={size} width={size} />;
    case EmptyStateIcon.HEART:
      return <HeartIcon height={size} width={size} />;
    case EmptyStateIcon.CALENDAR:
      return <CalendarIcon height={size} width={size} />;
    case EmptyStateIcon.NONE:
      return null;
    default:
      return null;
  }
});
